<template>
  <div v-rect-loading="loading" class="ai-summary-info">
    <InfoCard
      class="summary-content"
      icon="iconfont icona-ResumeSummary2-09"
      :title="$t(titleI18nKey)"
      :infoList="infoList"
      :infoLines="infoLines"
    >
      <template #copyBtn="{ label }">
        <span class="label-inner">{{ label }}</span>
        <el-tooltip
          effect="light"
          popper-class="light-tooltip-popper"
          :content="$t(`${i18nPrefix}.btn.aiResumeSummarizerCopy`)"
        >
          <div class="copy-btn" @click="handleCopySummary">
            <i class="btn-icon el-icon-document-copy"></i>
          </div>
        </el-tooltip>
      </template>
      <template #fitScore="{ label }">
        <span class="label-inner">{{ label }}</span>
        <div class="score-container">
          <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.tip.resumeFitInfo`)">
            <div ref="fitScore" class="chart-score"></div>
          </el-tooltip>
        </div>
      </template>
      <template #similarCandidateLabel="{ label }">
        <span class="label-inner">{{ label }}</span>
        <div class="more-btn" @click="onMoreSimilarCandidateBtnClick">
          <span class="btn-label iconfont icontwins"></span>
        </div>
      </template>
      <template #similarCandidate>
        <div class="similar-candidate-list">
          <transition :name="`slide-${similarSlideDirection}`" mode="out-in">
            <div
              v-if="showSimilarCandidate"
              :key="`similar-candidate-page-${similarityInfo.infoPage}`"
              class="similar-candidate-list-inner"
            >
              <div
                v-for="(info) in similarityInfoList"
                :key="info.id"
                class="similar-candidate"
              >
                <el-popover
                  trigger="hover"
                  placement="left"
                  :popper-class="`similarity-summary-popover ${info.similaritySummary?.loading ? 'is-loading' : ''}`"
                  :ref="`similarityPopover_${info.id}`"
                  :openDelay="1000"
                  :disabled="similarityInfo.loading"
                  @after-enter="onAfterEnter(info)"
                  @show="onShow(info)"
                  @hide="onHidden(info)"
                >
                  <template #reference>
                    <div class="avatar-container" :class="info.is_agency_uploaded_and_hid && 'is-lock'" @click="onSimilarCandidateClick(info)">
                      <CircularLoading
                        v-model="info.avatarLoading"
                        size="44px"
                        :showCircle="info.showCircle && info.showPopover"
                        :color="info.showCircle && '#0096cc'"
                        :strokeWidth="info.showCircle ? '2px' : '3px'"
                        :contentGap="info.showCircle ? '3px' : '0px'"
                      >
                        <img class="avatar" :src="getCandidateAvatarPath(info)" @error.once="getDefaultProfilePhoto" />
                        <i v-if="info.is_agency_uploaded_and_hid" class="avatar-lock-icon iconfont iconLock"></i>
                      </CircularLoading>
                    </div>
                  </template>
                  <div class="similarity-container">
                    <div class="similarity-chart" :ref="`similarityContainer_${info.id}`"></div>
                    <div class="similarity-summary" element-loading-background="transparent">{{ info.similaritySummary?.content }}</div>
                  </div>
                </el-popover>
                <template v-if="!similarityInfo.loading">
                  <div v-auto-tooltip class="name-container">{{ info.candidate.name }}</div>
                  <div v-if="!isTalentPool" v-auto-tooltip="`${info.job.id} - ${info.job.i18nName}`" class="applied-job-container">{{ info.job.i18nName }}</div>
                  <div v-auto-tooltip class="last-work-container">{{ info.recent_work_experience?.company }}</div>
                </template>
              </div>
            </div>
          </transition>
          <div v-if="!showSimilarCandidate" class="empty-similar-candidate-list">{{ $t('el.table.emptyText') }}</div>
        </div>
      </template>
    </InfoCard>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import InfoCard from '@/components/candidate/infoCard.vue'
import { generateFitEchartsOption } from '@/constant/application'
import { mapActions } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import { getScoreColor } from '@/utils/common'
import CircularLoading from '@/components/loading/circularLoading.vue'

export default {
  components: {
    CircularLoading,
    InfoCard
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    titleI18nKey: {
      type: String,
      default: ''
    },
    aiSummaryContent: {
      type: String,
      default: ''
    },
    aiMatchSummaryContent: {
      type: String,
      default: ''
    },
    fitScore: {
      default: null
    },
    fitDisplayScore: {
      default: null
    },
    fitDisplayColor: {
      type: String,
      default: ''
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    candidateBaseInfo: {
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      autoContent: '',
      showSimilarCandidate: true,
      similarSlideDirection: 'left',
      similarityInfo: {
        loading: false,
        resumeText: null,
        dataList: [],
        totalInfoList: [],
        infoPageSize: 5,
        infoPage: 1,
        get infoList () {
          const end = this.infoPage * this.infoPageSize
          const start = end - this.infoPageSize
          return this.totalInfoList.slice(start, end)
        },
        similarityMap: {},
        candidateId: null,
        jobId: null
      }
    }
  },
  computed: {
    candidateId () {
      return this.$route.query.candidate_id
    },
    jobId () {
      return this.$route.query.job_id
    },
    infoList () {
      return [{
        aiSummary: this.aiSummaryContent,
        aiMatchSummary: this.aiMatchSummaryContent
        // 应该存在 similarCandidate: this.similarityInfo 的赋值，单是这里 similarCandidate 的整个 content 都是通过 slot 自定义的，
        // 所以这里可以直接省略
      }]
    },
    hasSimilarCandidate () {
      return this.hasAnyPermission(['PERM_VIEW_SIMILAR_CANDIDATE', 'PREMIUM_SIMILAR_CANDIDATE'])
    },
    infoLines () {
      const vm = this
      const list = {
        aiSummary: {
          get hidden () { return !vm.aiSummaryContent },
          labelI18nKey: `${this.i18nPrefix}.title.aiResumeSummarizer`,
          labelSlotName: 'copyBtn',
          className: 'ai-summary',
          fullLine: true,
          multipleText: true,
          bottomLine: true
        },
        aiMatchSummary: {
          get hidden () { return !vm.aiMatchSummaryContent },
          labelI18nKey: `${this.i18nPrefix}.title.aiResumeFitScore`,
          labelSlotName: 'fitScore',
          className: 'ai-match-summary',
          fullLine: true,
          multipleText: true,
          bottomLine: true
        },
        similarCandidate: {
          get hidden () { return !vm.hasSimilarCandidate },
          labelI18nKey: `${this.i18nPrefix}.title.similarCandidate`,
          labelSlotName: 'similarCandidateLabel',
          className: 'similar-candidate-container',
          fullLine: true,
          contentName: 'similarCandidate'
        }
      }
      return list
    },
    similarityInfoList () {
      return this.similarityInfo.loading
        ? _.chain(-1).range(-6).map(index => ({ id: index, avatarLoading: true })).value()
        : this.similarityInfo.infoList
    }
  },
  created () {
    this.refreshSimilarityInfo()
  },
  mounted () {
    this.initFitScore()
  },
  methods: {
    ...mapActions('application', ['getCompanyApplicationList']),
    ...mapActions('talent', ['getCompanyTalentCandidateList']),
    ...mapActions('resumeVector', ['getSimilarApplication', 'getSimilarTalent', 'getApplicationSimilaritySummary', 'getTalentSimilaritySummary']),
    handleCopySummary () {
      this.$copyText(this.aiSummaryContent).then(() => {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.aiSummaryCopySuccess`))
      })
    },
    getCandidateAvatarPath (info) {
      if (this.similarityInfo.loading) {
        return this.$DjangoStaticUrl('images_en/default.jpg')
      }
      return info.is_agency_uploaded_and_hid ? this.$DjangoStaticUrl('images_en/default.jpg') : this.$DjangoBaseUrl(info.candidate.photo)
    },
    initFitScore () {
      const el = this.$refs.fitScore
      if (!el) {
        return
      }
      const val = this.fitScore
      const color = this.fitDisplayColor || ''
      const displayValue = this.fitDisplayScore || val
      const option = generateFitEchartsOption(val, displayValue, color, 14)
      const series = option.series
      series.axisLine.lineStyle.width = 2
      const chart = echarts.init(el)
      chart.setOption(option)
      this.$nextTick(() => {
        chart.resize()
      })
    },
    async refreshPopover (info) {
      let el = this.$refs[`similarityPopover_${info.id}`]
      if (!el) {
        await this.$nextTick()
        el = this.$refs[`similarityPopover_${info.id}`]
      }
      if (!el) {
        return
      }
      if (_.isArrayLike(el)) {
        el = el[0]
      }
      el.updatePopper()
    },
    startLoadSimilaritySummary (info) {
      const summary = info.similaritySummary
      let func = null
      const params = {
        language: summary.language,
        streaming: true,
        onStart: () => {
          // summary.loading = false
        },
        onProgress: (data) => {
          if (data.type === 'text') {
            if (data.content) {
              summary.loading = false
              if (!summary.content) {
                summary.content = ''
              }
              summary.content += data.content
              this.refreshPopover(info)
            }
          }
        },
        onEnd: () => {
          summary.loading = false
          summary.finished = true
          summary.error = false
          info.avatarLoading = false
          info.showCircle = true
        },
        onError: () => {
          summary.loading = false
          summary.finished = false
          summary.error = true
          info.avatarLoading = false
          this.$message.error(this.$t('error.message.serverError'))
        }
      }
      if (this.isTalentPool) {
        func = this.getTalentSimilaritySummary
        params.resume_list = JSON.stringify([
          { candidate_id: this.candidateId },
          { candidate_id: info.candidate.id }
        ])
      } else {
        func = this.getApplicationSimilaritySummary
        params.resume_list = JSON.stringify([
          { job_id: this.jobId, candidate_id: this.candidateId },
          { job_id: info.job.id, candidate_id: info.candidate.id }
        ])
      }
      func(params)
    },
    onShow (info) {
      const language = isEn() ? 'en' : 'zh-cn'
      info.showPopover = true
      if (info.similaritySummary?.loading) {
        return
      }
      if (language === info.similaritySummary?.language && info.similaritySummary?.finished) {
        info.showCircle = true
        return
      }
      info.avatarLoading = true
      info.similaritySummary.loading = true
      info.similaritySummary.language = language
      this.startLoadSimilaritySummary(info)
    },
    onHidden (info) {
      info.showPopover = false
      info.avatarLoading = false
    },
    async onAfterEnter (info) {
      let el = this.$refs[`similarityContainer_${info.id}`]
      if (!el) {
        await this.$nextTick()
        el = this.$refs[`similarityContainer_${info.id}`]
      }
      if (_.isArrayLike(el)) {
        el = el[0]
      }
      if (!el) {
        return
      }
      const similarity = info.similarity
      const color = getScoreColor(similarity)
      const option = generateFitEchartsOption(parseInt(similarity), similarity, color, 14)
      const series = option.series
      series.axisLine.lineStyle.width = 2
      const chart = echarts.init(el)
      chart.setOption(option)
      chart.resize()
    },
    async loadSimilarCandidate () {
      const info = this.similarityInfo

      const candidateId = info.candidateId = this.candidateId
      const jobId = info.candidateId = this.jobId

      let resp
      if (this.isTalentPool) {
        resp = await this.getSimilarTalent({
          top: 500,
          candidate_id: candidateId
        })
      } else {
        resp = await this.getSimilarApplication({
          top: 500,
          candidate_id: candidateId,
          job_id: jobId
        })
      }

      info.resumeText = resp?.resume_text || ''
      info.dataList = resp?.data_list

      const similarityMap = info.similarityMap = {}
      if (this.isTalentPool) {
        _.forEach(info.dataList, item => {
          const candidateId = item.candidate_id
          similarityMap[candidateId] = Math.round((1 - item.difference) * 10000) / 100
        })
      } else {
        _.forEach(info.dataList, item => {
          const candidateId = item.candidate_id
          const jobId = item.job_id
          if (!similarityMap[jobId]) {
            similarityMap[jobId] = {}
          }
          similarityMap[jobId][candidateId] = Math.round((1 - item.difference) * 10000) / 100
        })
      }
    },
    async refreshSimilarityInfo () {
      const info = this.similarityInfo
      if (!this.hasSimilarCandidate) {
        return
      }
      this.showSimilarCandidate = true
      info.loading = true
      try {
        let needReLoad = false
        if (this.isTalentPool) {
          needReLoad = info.candidateId !== this.candidateId
        } else {
          needReLoad = info.candidateId !== this.candidateId || info.jobId !== this.jobId
        }
        if (needReLoad) {
          await this.loadSimilarCandidate()
        }
        if (!info.resumeText || !info.dataList) {
          info.dataList = []
          info.totalInfoList = []
          this.showSimilarCandidate = false
          return
        }
        const similarityMap = info.similarityMap
        let infoListChain
        let workYear = 0
        const totalWorkYear = this.candidateBaseInfo?.total_work_year || 0
        if (totalWorkYear <= 3) {
          workYear = 2
        } else if (totalWorkYear <= 7) {
          workYear = 3
        } else if (totalWorkYear <= 12) {
          workYear = 4
        } else {
          workYear = 5
        }
        const minWorkYear = totalWorkYear - workYear
        const maxWorkYear = totalWorkYear + workYear
        if (this.isTalentPool) {
          const appResp = await this.getCompanyTalentCandidateList({
            candidate_id: _.chain(info.dataList).map('candidate_id').uniq().join(',').value(),
            page_size: 10,
            work_year_min: minWorkYear < 0 ? 0 : minWorkYear,
            work_year_max: maxWorkYear
          })
          infoListChain = _.chain(appResp.data).filter(item => {
            item.similarity = similarityMap[item.candidate.id]
            return _.has(similarityMap, item.candidate.id)
          })
        } else {
          const appResp = await this.getCompanyApplicationList({
            candidate_apply_job_id: _.chain(info.dataList).map('candidate_apply_job_id').uniq().join(',').value(),
            page_size: 10,
            work_year_min: minWorkYear < 0 ? 0 : minWorkYear,
            work_year_max: maxWorkYear
          })
          infoListChain = _.chain(appResp.data).filter(item => {
            item.similarity = similarityMap[item.job.id]?.[item.candidate.id]
            addI18nProperty(item.job, 'i18nName', 'cn_title', 'title')
            return _.has(similarityMap[item.job.id], item.candidate.id)
          })
        }
        info.totalInfoList = infoListChain
          .orderBy(['similarity'], ['desc'])
          .map(item => ({
            ...item,
            avatarLoading: false,
            showCircle: false,
            showPopover: false,
            similaritySummary: {
              loading: false,
              language: null,
              content: null,
              finished: false,
              error: false
            }
          }))
          .value()
      } finally {
        info.loading = false
      }
    },
    getDefaultProfilePhoto (e) {
      e.target.src = this.$DjangoStaticUrl('images_en/default.jpg')
    },
    onMoreSimilarCandidateBtnClick () {
      const info = this.similarityInfo
      const maxPage = Math.ceil(info.totalInfoList.length / info.infoPageSize)
      if (maxPage <= 1) {
        return
      }
      const nextPage = info.infoPage + 1 > maxPage ? 1 : info.infoPage + 1
      this.similarSlideDirection = nextPage === 1 && info.infoPage === maxPage ? 'right' : 'left'
      info.infoPage = nextPage
    },
    onSimilarCandidateClick (info) {
      if (this.isTalentPool) {
        const candidateId = info.candidate.id
        const backUrl = encodeURIComponent(this.$route.fullPath)
        window.open(`/v-admin/talent-pool/candidate/profile?candidate_id=${candidateId}&list_params=${encodeURIComponent(this.$route.query.list_params || '')}&back=${backUrl}`)
      } else {
        const candidateId = info.candidate.id
        const jobId = info.job.id
        const backUrl = encodeURIComponent(this.$route.fullPath)
        window.open(`/v-admin/application/profile?candidate_id=${candidateId}&job_id=${jobId}&list_params=${encodeURIComponent(this.$route.query.list_params || '')}&page_from=applicant&back=${backUrl}`)
      }
    }
  },
  watch: {
    aiMatchSummaryContent () {
      this.$nextTick(() => {
        this.initFitScore()
      })
    },
    aiSummaryContent () {
      this.$nextTick(() => {
        this.initFitScore()
      })
    },
    $route () {
      this.refreshSimilarityInfo()
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-summary-info {
  .summary-content {
    .label-inner {
      display: inline-block;
      width: calc(100% - 30px);
      vertical-align: middle;
    }
    .ai-summary {
      .copy-btn {
        display: inline-block;
        margin-left: 10px;
        vertical-align: middle;
        color: #0096cc;
        .btn-icon {
          font-size: 18px;
          cursor: pointer;
        }
      }
    }
    .ai-match-summary {
      .label-inner {
        width: calc(100% - 40px);
      }
      .score-container {
        display: inline-block;
        margin-left: 10px;
        vertical-align: middle;
        .chart-score {
          width: 30px;
          height: 30px;
        }
      }
    }
    .similar-candidate-container {
      .label-inner {
        width: calc(100% - 34px);
      }
      .more-btn {
        display: inline-block;
        margin-left: 10px;
        color: #0096cc;
        cursor: pointer;
        .btn-label {
          font-size: 18px;
        }
      }
      .similar-candidate-list {
        min-height: 50px;
        max-height: 124px;
        width: 100%;
        margin-top: 10px;
        overflow: hidden;
        // position: relative;

        .similar-candidate-list-inner {
          display: grid;
          grid-template-columns: repeat(5, minmax(auto, 88px));
          justify-content: space-between;
          column-gap: 8px;
          min-height: 50px;
          max-height: 124px;
          width: 100%;
          overflow: auto;
        }

        .slide-left-enter-active,
        .slide-left-leave-active,
        .slide-right-enter-active,
        .slide-right-leave-active {
          transition: transform .3s ease, opacity .3s ease;
        }
        .slide-left-enter,
        .slide-right-leave-to {
          transform: translateX(20px);
          opacity: 0;
        }
        .slide-left-leave-to,
        .slide-right-enter {
          transform: translateX(-20px);
          opacity: 0;
        }

        .similar-candidate {
          display: flex;
          flex-shrink: 0;
          width: 88px;
          align-items: center;
          font-size: 12px;
          line-height: 18px;
          flex-direction: column;
          .avatar-container {
            height: 44px;
            border-radius: 50%;
            cursor: pointer;
            position: relative;
            .avatar {
              border-radius: 50%;
              width: 100%;
              height: 100%;
            }
            .avatar-lock-icon {
              position: absolute;
              bottom: 2px;
              right: 4px;
              font-size: 14px;
              color: #9e9e9e;
            }
          }
          .name-container {
            max-width: 100%;
            margin-top: 4px;
            font-weight: bold;
            color: #060101;
            word-break: keep-all;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .applied-job-container {
            max-width: 100%;
            flex-shrink: 0;
            word-break: keep-all;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .last-work-container {
            max-width: 100%;
            flex-shrink: 0;
            word-break: keep-all;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        .empty-similar-candidate-list {
          line-height: 50px;
          text-align: center;
        }

        &.is-loading {
          height: 98px;

          .similar-candidate-list-inner {
            justify-items: center;
          }
        }
      }
    }
  }
}
.similarity-container {
  position: relative;
  .similarity-chart {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #fff;
    transform: translate(50%, -50%);
    position: absolute;
    top: -12px;
    right: -12px;
  }
  .similarity-summary {
    min-height: 30px;
    max-width: 26vw;
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
<style lang="scss">
.similarity-summary-popover {
  &.el-popper[x-placement] {
    border-radius: 2px;
    box-shadow: 0px 6px 16px 0px rgba(0,0,0,0.08),
                0px 3px 6px -4px rgba(0,0,0,0.12);
    background: #ededed;

    .popper__arrow {
      &::after {
        border-left-color: #ededed;
      }
    }
  }

  &.is-loading {
    display: none;
  }

}
</style>
